import { taskIsSleeping } from "../taskAttention"
import { makeAutoObservable, runInAction } from "mobx"
import { actionInputFingerprint } from "../../actions/actionRuns"
import type { SidebarPullRequestAutomation } from "../../components/sidebar/taskDisplayTypes"
import { MAX_TASK_ACTION_RUNS, type TaskActionRun, type TaskQueuePriority, type TaskRecord, type TaskSessionRef } from "../../persistence/records"
import { fallbackTitle, generateTitle } from "../../prompts/titleExtractor"
import { isHarnessId, providerIdForHarness } from "../../modelRegistry"
import type { Task, TaskDirectorySelection } from "../../types"
import { requestWildLarkMethod } from "../../wildlark/client"
import { TaskModel } from "../TaskModel"
import type { CodeStore } from "../store"
import { latestTaskActivityAt, latestTaskActivityAtFromTimestamps, readTimestampForActivity, unreadTimestampForActivity } from "../taskActivity"
import { continuationProviderSession, harnessIdFromSessionRef, taskFromProductRecord } from "../taskRecordAdapter"
import { ScheduledTurnManager } from "./ScheduledTurnManager"
import { TaskUIStateManager } from "./TaskUIStateManager"

// ============================================================================
// Deep Delete Types
// ============================================================================

export interface TaskResourceInventory {
    taskId: string
    taskTitle: string
    isRunning: boolean
    snapshotIds: string[]
    images: Array<{ id: string; ext: string }>
    sessions: Array<{ sessionId: string; harnessId: string }>
}

export interface DeleteOptions {
    deleteSnapshots: boolean
    deleteImages: boolean
    deleteSessions: boolean
}

interface SidebarTaskProjection {
    id: string
    title: string
    closed?: boolean
    closedAt?: string
    createdAt: string
    updatedAt: string
    lastViewedAt?: string
    lastActivityAt?: string
    queuePriority?: TaskQueuePriority
    scheduledCount?: number
    nextScheduledAt?: string
    scheduledNeedsAttention?: boolean
    attention?: TaskRecord["attention"]
    sleeping?: boolean
    pullRequestAutomation?: SidebarPullRequestAutomation
}

function sidebarTaskProjection(record: TaskRecord, scheduled: { count: number; nextAt?: string; hasAttention: boolean }, now: number): SidebarTaskProjection {
    const automation = record.pullRequestAutomation
    const automatedLink = automation ? record.externalLinks?.find((link) => link.id === automation.linkId) : undefined
    return {
        id: record.id,
        title: record.title,
        closed: record.closed,
        closedAt: record.closedAt,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
        lastViewedAt: record.lastViewedAt,
        lastActivityAt: latestTaskActivityAt(record, now),
        attention: record.attention,
        sleeping: taskIsSleeping(record, now),
        queuePriority: record.queuePriority,
        ...(scheduled.count > 0 ? { scheduledCount: scheduled.count } : {}),
        ...(scheduled.nextAt ? { nextScheduledAt: scheduled.nextAt } : {}),
        ...(scheduled.hasAttention ? { scheduledNeedsAttention: true } : {}),
        ...(automation
            ? {
                  pullRequestAutomation: {
                      linkId: automation.linkId,
                      number: automatedLink?.number,
                      phase: automation.phase,
                      ...(automation.hold ? { hold: { reason: automation.hold.reason } } : {}),
                      ...(automation.delivery ? { delivery: { operation: automation.delivery.operation } } : {}),
                  },
              }
            : {}),
    }
}

function providerIdFromSessionKey(key: string): TaskSessionRef["providerId"] {
    if (isHarnessId(key)) return providerIdForHarness(key)
    if (key === "grok") return "grok"
    return key === "codex" ? "codex" : "claude"
}

export class TaskManager {
    tasksLoading = false
    loadedRepoIds: Set<string> = new Set()
    regeneratingTitleTaskIds: Set<string> = new Set()
    readonly scheduledTurns: ScheduledTurnManager
    private taskModels: Map<string, TaskModel> = new Map()
    private taskUIStates: Map<string, TaskUIStateManager> = new Map()

    constructor(private store: CodeStore) {
        this.scheduledTurns = new ScheduledTurnManager(store)
        makeAutoObservable<TaskManager, "store">(this, {
            loadedRepoIds: true,
            scheduledTurns: false,
            store: false,
        })
    }

    getTask(taskId: string): Task | null {
        const record = this.store.productState?.tasks.get(taskId)
        return record ? taskFromProductRecord(record, this.store.currentUser) : null
    }

    getTasksForRepo(repoId: string): Task[] {
        return [...(this.store.productState?.tasks.values() ?? [])]
            .filter((record) => record.repoId === repoId)
            .map((record) => taskFromProductRecord(record, this.store.currentUser))
    }

    getSidebarTasksForRepo(repoId: string): SidebarTaskProjection[] {
        const productState = this.store.productState
        if (!productState) return []

        return [...productState.tasks.values()]
            .filter((record) => record.repoId === repoId)
            .map((record) => sidebarTaskProjection(record, this.scheduledTurns.summaryForTask(record.id), this.store.productState?.attentionNow ?? Date.now()))
    }

    getTaskModel(taskId: string): TaskModel | null {
        if (!this.getTask(taskId)) return null

        const cached = this.taskModels.get(taskId)
        if (cached) {
            return cached
        }

        const model = new TaskModel(this.store, taskId)
        this.taskModels.set(taskId, model)
        return model
    }

    getTaskUIState(taskId: string): TaskUIStateManager {
        const cached = this.taskUIStates.get(taskId)
        if (cached) {
            return cached
        }

        const uiState = new TaskUIStateManager()
        this.taskUIStates.set(taskId, uiState)
        return uiState
    }

    invalidateTaskModel(taskId: string): void {
        const model = this.taskModels.get(taskId)
        if (model) {
            model.dispose()
        }
        this.taskModels.delete(taskId)
    }

    reconcileMissingRuntimeTurns(taskId: string, turnIds: readonly string[]): void {
        this.taskModels.get(taskId)?.reconcileMissingRuntimeTurns(turnIds)
    }

    ensureTasksLoaded(repoId: string): void {
        // Sidebar task records are loaded through product state.
        this.loadedRepoIds.add(repoId)
    }

    async removeTask(id: string): Promise<void> {
        await this.deepRemoveTask(id, {
            deleteSnapshots: false,
            deleteImages: false,
            deleteSessions: false,
        })
    }

    // ==================== Deep Delete ====================

    private async updateTaskRecord(
        taskId: string,
        apply: (draft: TaskRecord, timestamp: string) => void,
        options: { invalidateModel?: boolean; titleIntent?: "manual"; lifecycle?: { closed: boolean; closedAt?: string } } = {}
    ): Promise<boolean> {
        const productState = this.store.productState
        const record = productState?.tasks.get(taskId)
        if (!productState || !record) {
            console.warn("[TaskManager] Cannot update task record - not found:", taskId)
            return false
        }
        const updatedAt = new Date().toISOString()
        const next: TaskRecord = {
            ...record,
            updatedAt,
        }
        apply(next, updatedAt)
        if (options.titleIntent || options.lifecycle) await productState.putTask(next, { titleIntent: options.titleIntent, lifecycle: options.lifecycle })
        else await productState.putTask(next)
        if (options.invalidateModel) this.invalidateTaskModel(taskId)
        return true
    }

    async getResourceInventory(ids: string[]): Promise<TaskResourceInventory[]> {
        const results: TaskResourceInventory[] = []

        for (const id of ids) {
            const record = this.store.productState?.tasks.get(id)
            if (!record) continue
            const task = taskFromProductRecord(record, this.store.currentUser)

            const snapshotIds: string[] = []
            const images: Array<{ id: string; ext: string }> = []
            const sessions = new Map<string, string>()

            for (const session of record.sessions) {
                const sessionId = session.nestSessionId ?? session.providerSessionId
                if (!sessionId) continue
                sessions.set(sessionId, harnessIdFromSessionRef(session))
            }

            results.push({
                taskId: id,
                taskTitle: task.title || task.description || "Untitled",
                isRunning: this.store.isTaskRunning(id),
                snapshotIds,
                images,
                sessions: [...sessions.entries()].map(([sessionId, harnessId]) => ({
                    sessionId,
                    harnessId,
                })),
            })
        }

        return results
    }

    async deepRemoveTasks(ids: string[], options: DeleteOptions): Promise<void> {
        for (const id of ids) {
            await this.deepRemoveTask(id, options)
        }
    }

    async deepRemoveTask(id: string, options: DeleteOptions): Promise<void> {
        const productState = this.store.productState
        const record = productState?.tasks.get(id)
        if (!productState || !record) {
            console.warn("[TaskManager] deepRemoveTask: Cannot find product-state task", id)
            return
        }

        await this.cleanupDeletedTaskResources(record, options)
        const deletion = await requestWildLarkMethod("wildlark.task.delete", {
            taskId: id,
            workspaceId: record.workspaceId,
        })
        if (!deletion.deleted || deletion.failed > 0) {
            throw new Error(
                `Could not delete the task (requested ${deletion.requested}, succeeded ${deletion.succeeded}, failed ${deletion.failed}): ${deletion.errors
                    .slice(0, 3)
                    .join("; ")}`
            )
        }
        this.scheduledTurns.forgetTask(record)
        productState.acceptTaskDeleted(id)

        // Clean up pinned state
        const pinned = this.store.personalSettingsStore?.settings.current.pinnedTaskIds
        if (pinned?.includes(id)) {
            this.store.personalSettingsStore?.settings.set({
                pinnedTaskIds: pinned.filter((pid) => pid !== id),
            })
        }

        const model = this.taskModels.get(id)
        if (model) {
            model.dispose()
        }
        runInAction(() => {
            this.taskModels.delete(id)
            this.taskUIStates.delete(id)
        })
        this.store.runtimes.removeTask(id)
        void this.store.timelines.deleteTask(id, record.workspaceId).catch((error) => {
            console.warn("[TaskManager] Failed to remove the isolated task timeline", { taskId: id, error })
        })
    }

    private async cleanupDeletedTaskResources(record: TaskRecord, options: DeleteOptions): Promise<void> {
        if (options.deleteSessions && record.sessions.length > 0) {
            console.warn("[TaskManager] Provider session deletion is not migrated to Nest; preserving provider session data", { taskId: record.id })
        }
    }

    // ==================== Session Management ====================

    async setSessionId({ taskId, key, sessionId }: { taskId: string; key: string; sessionId: string }): Promise<void> {
        await this.updateTaskRecord(taskId, (draft, updatedAt) => {
            if (!sessionId) return
            const sessions = draft.sessions.map((session) => ({ ...session }))
            const existingIndex = sessions.findIndex((session) => harnessIdFromSessionRef(session) === key)
            const providerId = providerIdFromSessionKey(key)
            const existing = existingIndex >= 0 ? sessions[existingIndex] : null
            const nextSession: TaskSessionRef = {
                ...existing,
                role: existing?.role ?? (sessions.length === 0 ? "primary" : "auxiliary"),
                providerId,
                nestSessionId: sessionId,
                updatedAt,
            }
            if (existingIndex >= 0) {
                sessions[existingIndex] = nextSession
            } else {
                sessions.push(nextSession)
            }
            draft.sessions = sessions
        })
    }

    async markTaskViewed(taskId: string, activityAt?: string): Promise<void> {
        // A guest reads someone else's task records and cannot mark them.
        if (this.store.isGuest) return
        const productState = this.store.productState
        const productStateTask = productState?.tasks.get(taskId)
        if (productState && productStateTask) {
            const viewedAt = readTimestampForActivity(activityAt ?? latestTaskActivityAt(productStateTask))
            void productState
                .putTask({ ...productStateTask, lastViewedAt: viewedAt })
                .catch((error) => console.error("[TaskManager] Failed to mark task viewed in product state:", error))
            return
        }
    }

    async markTaskUnread(taskId: string): Promise<void> {
        if (this.store.isGuest) return
        const productState = this.store.productState
        const productStateTask = productState?.tasks.get(taskId)
        if (productState && productStateTask) {
            const next: TaskRecord = { ...productStateTask }
            const unreadAt = unreadTimestampForActivity(latestTaskActivityAt(productStateTask))
            if (unreadAt) next.lastViewedAt = unreadAt
            else next.lastViewedAt = undefined
            void productState.putTask(next).catch((error) => console.error("[TaskManager] Failed to mark task unread in product state:", error))
            return
        }
    }

    recordNestSessionActivity({
        taskId,
        sessionId,
        activityAt,
        thinking,
    }: {
        taskId: string
        sessionId: string | undefined
        activityAt: string | undefined
        thinking?: import("../../agentTarget").ThinkingLevel
    }): void {
        if (!sessionId || !activityAt) return

        const productState = this.store.productState
        const productStateTask = productState?.tasks.get(taskId)
        if (!productState || !productStateTask) return

        let changed = false
        const sessions = productStateTask.sessions.map((session) => {
            if (session.nestSessionId !== sessionId && session.providerSessionId !== sessionId) return session

            const updatedAt = latestTaskActivityAtFromTimestamps([session.updatedAt, session.createdAt, activityAt])
            if (!updatedAt || (updatedAt === session.updatedAt && (thinking === undefined || thinking === session.thinking))) return session

            changed = true
            return { ...session, updatedAt, ...(thinking ? { thinking } : {}) }
        })
        if (!changed) return

        const updatedAt = latestTaskActivityAtFromTimestamps([productStateTask.updatedAt, activityAt]) ?? productStateTask.updatedAt
        void productState
            .putTask({ ...productStateTask, sessions, updatedAt })
            .catch((error) => console.error("[TaskManager] Failed to record task session activity:", error))
    }

    recordSavedActionRun({
        taskId,
        sessionId,
        turnId,
        clientRequestId,
        input,
        actions,
        createdAt,
    }: {
        taskId: string
        sessionId: string
        turnId: string
        clientRequestId: string
        input: string
        actions: TaskActionRun["actions"]
        createdAt: string
    }): void {
        const productState = this.store.productState
        const record = productState?.tasks.get(taskId)
        if (!productState || !record) return

        const run: TaskActionRun = {
            clientRequestId,
            sessionId,
            turnId,
            inputFingerprint: actionInputFingerprint(input),
            actions,
            createdAt,
        }
        const actionRuns = [...(record.actionRuns ?? []).filter((item) => item.clientRequestId !== clientRequestId), run].slice(-MAX_TASK_ACTION_RUNS)
        const updatedAt = latestTaskActivityAtFromTimestamps([record.updatedAt, createdAt]) ?? record.updatedAt
        void productState
            .putTask({ ...record, actionRuns, updatedAt })
            .catch((error) => console.error("[TaskManager] Failed to record saved Action run:", error))
    }

    rebindSavedActionRun({ taskId, clientRequestId, sessionId, turnId }: { taskId: string; clientRequestId: string; sessionId: string; turnId: string }): void {
        const productState = this.store.productState
        const record = productState?.tasks.get(taskId)
        if (!productState || !record?.actionRuns?.some((run) => run.clientRequestId === clientRequestId)) return

        const actionRuns = record.actionRuns.map((run) => (run.clientRequestId === clientRequestId ? { ...run, sessionId, turnId } : run))
        void productState.putTask({ ...record, actionRuns }).catch((error) => console.error("[TaskManager] Failed to rebind saved Action run:", error))
    }

    async setTaskClosed(taskId: string, closed: boolean): Promise<void> {
        const lifecycle = { closed, ...(closed ? { closedAt: new Date().toISOString() } : {}) }
        await this.updateTaskRecord(
            taskId,
            (draft) => {
                draft.closed = lifecycle.closed
                if (lifecycle.closedAt) draft.closedAt = lifecycle.closedAt
                else delete draft.closedAt
            },
            { lifecycle }
        )
        if (!closed && this.store.productState?.tasks.get(taskId)?.attention?.sleepUntil) await this.wakeTask(taskId)
    }

    setTaskQueuePriority(taskId: string, queuePriority: TaskQueuePriority | undefined): Promise<boolean> {
        return this.updateTaskRecord(taskId, (draft) => {
            draft.queuePriority = queuePriority
        })
    }

    async closeTask(taskId: string): Promise<void> {
        const productState = this.store.productState
        const record = productState?.tasks.get(taskId)
        if (!productState || !record) throw new Error("This task is no longer available")
        if (!record.pullRequestAutomation) {
            await this.setTaskClosed(taskId, true)
            return
        }

        const sessionId = continuationProviderSession(
            taskFromProductRecord(record, this.store.currentUser).providerSessions ?? [],
            record.activeSessionId
        )?.nestSessionId
        if (!sessionId) throw new Error("This task does not have a resumable session")
        await requestWildLarkMethod("wildlark.task.close", {
            taskId,
            workspaceId: record.workspaceId,
            sessionId,
        })
        await productState.refreshTask(taskId, record.workspaceId)
    }

    async sleepTask(taskId: string, until: string): Promise<void> {
        await this.changeTaskAttention(taskId, until)
    }

    async wakeTask(taskId: string): Promise<void> {
        await this.changeTaskAttention(taskId)
    }

    private async changeTaskAttention(taskId: string, until?: string): Promise<void> {
        const taskModel = this.getTaskModel(taskId)
        if (!taskModel) throw new Error("This task is no longer available")
        const sessionId = taskModel.primarySessionThreadReference?.sessionId
        if (!sessionId) throw new Error("This task does not have a resumable session")
        const binding = { taskId, workspaceId: taskModel.workspaceId, sessionId }
        if (until) await requestWildLarkMethod("wildlark.task.sleep", { ...binding, until })
        else await requestWildLarkMethod("wildlark.task.wake", binding)
        await this.store.productState?.refreshTask(taskId, taskModel.workspaceId)
    }

    async reopenForScheduledStart(taskId: string, sessionId: string, scheduleId: string, startedAt: string): Promise<void> {
        const record = this.store.productState?.tasks.get(taskId)
        if (!record) return
        await requestWildLarkMethod("wildlark.task.scheduledStart.reconcile", {
            taskId,
            workspaceId: record.workspaceId,
            sessionId,
            scheduleId,
            startedAt,
        })
    }

    setEnabledMcpServerIds(taskId: string, serverIds: string[]): void {
        void (async () => {
            await this.updateTaskRecord(taskId, (draft) => {
                if (serverIds.length > 0) {
                    draft.enabledMcpServerIds = [...serverIds]
                } else {
                    draft.enabledMcpServerIds = undefined
                }
            })
        })().catch((error) => {
            console.error("[TaskManager] Failed to update MCP server selection:", error)
        })
    }

    async setTaskDirectorySelection(taskId: string, selection: TaskDirectorySelection | undefined): Promise<boolean> {
        return this.updateTaskRecord(taskId, (draft) => {
            if (selection) {
                draft.directorySelection = selection
                const directories = draft.additionalDirs ?? []
                if (!directories.includes(selection.path)) draft.additionalDirs = [...directories, selection.path]
            } else {
                delete draft.directorySelection
            }
        })
    }

    setTaskTitle(taskId: string, title: string): void {
        const trimmed = title.trim()
        if (!trimmed) return
        const record = this.store.productState?.tasks.get(taskId)
        if (!record || record.title === trimmed) return
        const primarySession =
            (record.activeSessionId
                ? record.sessions.find((session) => session.nestSessionId === record.activeSessionId)
                : record.sessions.find((session) => session.role === "primary")) ?? record.sessions[0]
        const sessionId = primarySession?.nestSessionId ?? primarySession?.providerSessionId
        const createdAt = new Date().toISOString()

        void (async () => {
            const updated = await this.updateTaskRecord(
                taskId,
                (draft) => {
                    draft.title = trimmed
                    draft.titleManuallySet = true
                    draft.titleAgentInitialized = true
                },
                { titleIntent: "manual" }
            )
            if (!updated) return
            try {
                await this.store.timelines.appendChapter({
                    taskId,
                    workspaceId: record.workspaceId,
                    title: trimmed,
                    source: "user",
                    createdAt,
                    ...(sessionId ? { anchor: { sessionId } } : {}),
                })
            } catch (error) {
                console.warn("[TaskManager] Task title changed, but its timeline chapter could not be recorded", { taskId, error })
            }
        })().catch((error) => {
            console.error("[TaskManager] Failed to update task title:", error)
        })
    }

    toggleTaskPinned(taskId: string): void {
        const store = this.store.personalSettingsStore
        if (!store) return
        const current = store.settings.current.pinnedTaskIds ?? []
        const isPinned = current.includes(taskId)
        store.settings.set({
            pinnedTaskIds: isPinned ? current.filter((id) => id !== taskId) : [...current, taskId],
        })
    }

    async regenerateTitle(taskId: string): Promise<void> {
        const task = this.getTask(taskId)
        if (!task) return

        const description = task.description
        if (!description) return

        const repo = this.store.repos.getRepo(task.repoId)
        if (!repo) return

        const taskModel = this.getTaskModel(taskId)
        const harnessId = taskModel?.harnessId

        runInAction(() => this.regeneratingTitleTaskIds.add(taskId))
        try {
            const abortController = new AbortController()
            const generatedTitle = await generateTitle(description, abortController, {
                harnessId,
            })
            this.setTaskTitle(taskId, generatedTitle ?? fallbackTitle(description))
        } catch (err) {
            console.error("[TaskManager] Title regeneration failed:", err)
            this.setTaskTitle(taskId, fallbackTitle(description))
        } finally {
            runInAction(() => this.regeneratingTitleTaskIds.delete(taskId))
        }
    }
}
